import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { request } from '@/lib/http';
import { type OpcaoPesquisavel } from '@/ui/components/select-pesquisavel';
import {
  type CentroCustoResumo,
  type DefinirCentroCustoRequest,
  documentoDetalheSchema,
  paginaCentrosCustoSchema,
} from '@fiscoai/contracts';

/** Quantos centros de custo por busca no seletor (o resto se acha digitando). */
export const CENTROS_POR_BUSCA = 20;

export const rotuloCentroCusto = (cc: Pick<CentroCustoResumo, 'codigo' | 'descricao'>) =>
  `${cc.codigo} – ${cc.descricao}`;

export function opcaoDoCentroCusto(cc: CentroCustoResumo): OpcaoPesquisavel {
  return {
    valor: cc.id,
    rotulo: rotuloCentroCusto(cc),
    detalhe: cc.natureza === 'CUSTO' ? 'Custo' : 'Despesa',
  };
}

/** Busca remota dos centros de custo ATIVOS da empresa da nota (identidade estável). */
export function useBuscarCentrosCusto(empresaId: string) {
  return useCallback(
    async (termo: string, signal: AbortSignal) => {
      const pagina = await request(`/empresas/${empresaId}/centros-custo`, {
        schema: paginaCentrosCustoSchema,
        query: { q: termo || undefined, ativos: 'true', limit: CENTROS_POR_BUSCA },
        signal,
      });
      return pagina.items.map(opcaoDoCentroCusto);
    },
    [empresaId],
  );
}

/** Centro de custo da nota inteira e/ou de um item; a resposta (grupo refeito) vai ao cache. */
export function useDefinirCentroCusto(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: DefinirCentroCustoRequest) =>
      request(`/documentos/${id}/centro-custo`, {
        method: 'PUT',
        body: corpo,
        schema: documentoDetalheSchema,
      }),
    onSuccess: (detalhe) => {
      cliente.setQueryData(['documentos', 'detalhe', id], detalhe);
      void cliente.invalidateQueries({ queryKey: ['documentos'], refetchType: 'none' });
      void cliente.invalidateQueries({ queryKey: ['pendencias'] });
    },
  });
}
